import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Eye, 
  Trash2, 
  Download,
  Package,
  MapPin,
  Calendar,
  DollarSign,
  User,
  RefreshCw,
  HelpCircle,
  MoreHorizontal
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { assetTypes, departments } from '../../data/dummyData';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import ActionMenu from '../common/ActionMenu';
import { useAssets } from '../../context/AssetContext';
import { useToast, ToastContainer } from '../common/Toast';
import { assetService } from '../../services/assetService';
import AssetForm from './AssetForm';

// Project-style Action Dropdown (Asset Management)
const ActionDropdown = ({ asset, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setOpenUpward(window.innerHeight - rect.bottom < 120);
    }
    setIsOpen(prev => !prev);
  };

  return (
    <div
      ref={dropdownRef}
      className="relative"
      style={{ zIndex: isOpen ? 10000 : 'auto' }}
    >
      {/* Action Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
      >
        <MoreHorizontal size={16} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${window.innerHeight -
                  buttonRef.current.getBoundingClientRect().top +
                  8}px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                onClick={() => { setIsOpen(false); onView(asset); }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
              >
                <Eye size={16} className="mr-2.5 text-gray-500" />
                View
              </button>
            </li>

            <li>
              <button
                onClick={() => { setIsOpen(false); onEdit(asset); }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
              >
                <Edit size={16} className="mr-2.5 text-gray-500" />
                Edit
              </button>
            </li>

            <li>
              <button
                onClick={() => { setIsOpen(false); onDelete(asset); }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
              >
                <Trash2 size={16} className="mr-2.5 text-red-500" />
                Delete
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const AssetManagement = () => {
  const { 
    assets, 
    assetStats, 
    loading, 
    error, 
    pagination,
    createAsset, 
    updateAsset, 
    deleteAsset,
    setFilters,
    setPagination,
    clearError
  } = useAssets();
  
  const { toasts, removeToast, success, error: showError } = useToast();
  
  const [hiddenColumns, setHiddenColumns] = useState(['none']); // Hide ID column by default
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Test mode state
  const [isTestMode, setIsTestMode] = useState(process.env.NODE_ENV === 'development');
  const [testData, setTestData] = useState(null);

  // Test data generation functions
  const generateTestAssetData = () => {
    const testAssets = [
      {
        assetCode: `TEST-${Date.now()}`,
        name: 'Test Laptop - Dell XPS 15',
        type: 'IT Equipment',
        subtype: 'Laptop',
        department: 'IT Department',
        status: 'Active',
        purchaseDate: new Date().toISOString().split('T')[0],
        vendor: 'Dell Technologies',
        cost: 1299.99,
        depreciationMethod: 'Straight Line',
        location: {
          building: 'Main Office',
          floor: '2nd Floor',
          room: 'IT-201',
          address: '123 Business Street, Tech City, TC 12345',
          coordinates: {
            latitude: 40.7128,
            longitude: -74.0060
          }
        },
        assignedTo: 'John Smith'
      },
      {
        assetCode: `TEST-${Date.now() + 1}`,
        name: 'Test Office Chair - Ergonomic',
        type: 'Furniture',
        subtype: 'Office Chair',
        department: 'HR Department',
        status: 'Active',
        purchaseDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        vendor: 'Herman Miller',
        cost: 899.50,
        depreciationMethod: 'Straight Line',
        location: {
          building: 'Main Office',
          floor: '1st Floor',
          room: 'HR-105',
          address: '123 Business Street, Tech City, TC 12345',
          coordinates: {
            latitude: 40.7128,
            longitude: -74.0060
          }
        },
        assignedTo: 'Sarah Johnson'
      },
      {
        assetCode: `TEST-${Date.now() + 2}`,
        name: 'Test Server - HP ProLiant',
        type: 'IT Equipment',
        subtype: 'Server',
        department: 'IT Department',
        status: 'Under Maintenance',
        purchaseDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        vendor: 'Hewlett Packard',
        cost: 2499.00,
        depreciationMethod: 'Declining Balance',
        location: {
          building: 'Data Center',
          floor: 'Basement',
          room: 'DC-001',
          address: '123 Business Street, Tech City, TC 12345',
          coordinates: {
            latitude: 40.7128,
            longitude: -74.0060
          }
        },
        assignedTo: 'Mike Chen'
      }
    ];
    return testAssets[Math.floor(Math.random() * testAssets.length)];
  };

  // Test feature handlers
  const handleTestFeature = (action) => {
    switch (action) {
      case 'auto-fill':
        // Auto-fill the form with test data
        const generatedTestData = generateTestAssetData();
        setTestData(generatedTestData);
        success(`Test data loaded! Form auto-filled with "${generatedTestData.name}".`);
        //console.log('Auto-fill test data:', generatedTestData);
        break;
      case 'create-test':
        // Create a test asset
        createTestAsset();
        break;
      case 'validate-form':
        // Test form validation
        testFormValidation();
        break;
      case 'reset-form':
        // Reset form to default state
        resetForm();
        break;
      default:
        //console.log('Test feature:', action);
    }
  };

  const createTestAsset = async () => {
    try {
      const testData = generateTestAssetData();
      await createAsset(testData);
      success(`Test asset "${testData.name}" created successfully!`);
    } catch (error) {
      console.error('Error creating test asset:', error);
      showError(error.message || 'Failed to create test asset');
    }
  };

  const testFormValidation = () => {
    // Test form validation by trying to submit with empty fields
    success('Form validation test completed. Check console for details.');
    //console.log('Testing form validation...');
    //console.log('Required fields: Asset Code, Asset Name, Asset Type, Subtype, Department');
  };

  const resetForm = () => {
    // Reset form to default state
    setTestData(null);
    success('Form reset to default state');
    //console.log('Form reset completed');
  };

  // Define columns for DataTable - Optimized for better layout
  const columns = [
    {
      key: 'assetId',
      label: 'ID',
      width: '100px',
      render: (value, row) => (
        <div className="d-flex align-items-center gap-1">
          <Package size={14} style={{ color: 'var(--primary-color)' }} />
          <strong style={{ fontSize: '12px' }}>{value || row._id}</strong>
        </div>
      )
    },
    {
      key: 'name',
      label: 'Asset Name',
      width: '200px',
      render: (value, row) => (
        <div>
          <div className="fw-medium" style={{ fontSize: '14px' }}>{value}</div>
          <small className="text-muted" style={{ fontSize: '11px' }}>
            {row.location?.address || row.location?.building || 'No location'}
          </small>
        </div>
      )
    },
    {
      key: 'type',
      label: 'Type',
      width: '120px',
      render: (value) => (
        <span style={{ fontSize: '13px' }}>{value}</span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: '130px',
      type: 'badge',
      badgeClass: (value) => {
        const statusClasses = {
          'Active': 'badge-success',
          'Under Maintenance': 'badge-warning',
          'Inactive': 'badge-secondary',
          'Disposed': 'badge-danger'
        };
        return statusClasses[value] || 'badge-secondary';
      }
    },
    {
      key: 'assignedTo',
      label: 'Assigned To',
      width: '140px',
      render: (value) => {
        // Handle both string and object types for assignedTo
        let displayValue = 'Unassigned';
        if (value) {
          if (typeof value === 'object' && value.name) {
            displayValue = value.name;
          } else if (typeof value === 'string') {
            displayValue = value;
          }
        }
        
        return (
          <div className="d-flex align-items-center gap-1">
            <User size={12} style={{ color: 'var(--text-muted)' }} />
            <span style={{ fontSize: '13px' }}>
              {displayValue}
            </span>
          </div>
        );
      }
    },
    {
      key: 'currentValue',
      label: 'Value',
      width: '120px',
      type: 'currency',
      render: (value) => (
        <div className="d-flex align-items-center gap-1">
          <DollarSign size={12} style={{ color: 'var(--success-color)' }} />
          <span style={{ fontSize: '13px', fontWeight: '600' }}>${value.toLocaleString()}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '60px',
      render: (_, asset) => {
        if (!asset || !asset._id) return null;
        return (
        <div className="d-flex justify-content-center">
          <ActionDropdown
          asset={asset}
          onView={handleViewAsset}
          onEdit={handleEditAsset}
          onDelete={handleDeleteAsset}
        />
        </div>
       );
      }
    }
  ];

  const handleEditAsset = (asset) => {
    // Navigate to dedicated edit page
    window.location.href = `/assets/edit/${asset._id}`;
  };

  const handleViewAsset = (asset) => {
    // Navigate to view page
    const assetId = asset._id || asset.id;
    if (assetId) {
      window.location.href = `/assets/view/${assetId}`;
    } else {
      console.error('Asset ID not found:', asset);
      showError('Unable to view asset: ID not found');
    }
  };

  const handleDeleteAsset = (asset) => {
    setAssetToDelete(asset);
    setShowDeleteModal(true);
  };

  // Confirm delete asset
  const confirmDeleteAsset = async () => {
    if (!assetToDelete) return;
    
    setIsDeleting(true);
    
    try {
      await deleteAsset(assetToDelete._id);
      success('Asset deleted successfully!');
    setShowDeleteModal(false);
    setAssetToDelete(null);
    } catch (error) {
      console.error('Error deleting asset:', error);
      showError(error.message || 'Failed to delete asset');
    } finally {
    setIsDeleting(false);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setAssetToDelete(null);
    setIsDeleting(false);
  };

  const handleRowClick = (asset) => {
    // Navigate to dedicated edit page
    window.location.href = `/assets/edit/${asset._id}`;
  };

  const handleRefresh = () => {
    // Trigger a reload by clearing filters and resetting pagination
    setFilters({});
    setPagination({ ...pagination, page: 1 });
    // The context should automatically reload assets when filters/pagination change
    success('Assets refreshed');
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/assets/asset-management.html';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh assets list',
      disabled: loading
    },
    {
      label: 'Add Asset',
      icon: <Plus size={18} />,
      onClick: () => window.location.href = '/assets/create',
      variant: 'primary',
      tooltip: 'Add a new asset'
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Asset Management"
        description="Manage your organization's assets efficiently"
        actions={headerActions}
      />
      <div className="page-container">
      {/* Error Display */}
      {error && (
        <div className="alert alert-danger" role="alert">
          <strong>Error:</strong> {error}
          <button 
            type="button" 
            className="btn-close" 
            onClick={clearError}
            style={{ float: 'right' }}
          ></button>
        </div>
      )}

      {/* Asset Inventory Table */}
      <DataTable
        data={assets}
        columns={columns}
        title="Asset Inventory"
        searchable={true}
        sortable={true}
        exportable={true}
        pagination={true}
        pageSize={pagination.itemsPerPage}
        showActions={false}
        showCheckboxes={false}
        //onView={handleViewAsset}
        //onRowClick={handleRowClick}
        //onEdit={handleEditAsset}
        //onDelete={handleDeleteAsset}
        searchPlaceholder="Search assets by name, ID, or type..."
        loading={loading}
        emptyMessage="No assets found. Click 'Add Asset' to get started."
        initialHiddenColumns={hiddenColumns}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDeleteAsset}
        title="Delete Asset"
        message={`Are you sure you want to delete "${assetToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />
      
      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default AssetManagement;